// src/pages/ProductDetails.tsx
import React, { useState, useEffect, useMemo } from 'react';
import {
  Heart,
  ShoppingCart,
  ChevronLeft,
  ChevronRight,
  Share2,
  Star,
  Truck,
  Shield,
  RefreshCw
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { Product } from '../types';
import { ProductCard } from '../components/ProductCard';
import { toast } from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import { ProductSpecificationsModal } from '../components/ProductSpecificationsModal';
import './ProductDetails.css'; // Ensure to import the CSS file for styles
import parse from 'html-react-parser'; // Add this import
import { productsApi } from '../utils/api';

// 1. Base URL for your backend - update to use env variable
const backendBaseURL = import.meta.env.VITE_API_BASE_URL;
const backendBaseImagesURL = import.meta.env.VITE_API_BASE_IMAGES_URL;

// 2. Helper to convert stored URLs to full URLs
const getFullImageURL = (url: string): string => {
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${backendBaseImagesURL}${url}`;
};

// 3. This function retrieves the images specifically for the selected color-level variation
const getVariationSpecificImages = (
  product: Product,
  selectedColor?: string
): string[] => {
  // If no color is selected, return all product images
  if (!product.images || !selectedColor) {
    return product.images?.map((img) => getFullImageURL(img.url)) || [];
  }

  // Find the variation row corresponding to the selected color
  const colorVariation = product.variations?.stockByVariation.find(
    (v) => v.color === selectedColor
  );

  // If we cannot find a matching color variation, just return all images
  if (!colorVariation) {
    return product.images.map((img) => getFullImageURL(img.url)) || [];
  }

  // Otherwise, filter for images whose variation_id matches that color-level variation row
  const variationImages = product.images.filter(
    (img) => img.variation_id === parseInt(colorVariation.id)
  );

  // If none are color-level images, fall back to all images
  if (variationImages.length === 0) {
    return product.images.map((img) => getFullImageURL(img.url)) || [];
  }

  return variationImages.map((img) => getFullImageURL(img.url));
};

// 4. Helper to find available stock for the selected variation combination
const getAvailableStock = (product: Product, selectedVariations: any): number => {
  if (!product.variations || !selectedVariations) return product.stock;

  const variationType = product.variations.type; // e.g., "color-age"

  if (variationType === 'color-age') {
    const { color, ageRange } = selectedVariations;

    if (color) {
      // Find the parent variation based on selected color
      const colorVar = product.variations.stockByVariation.find(
        (v: any) => v.color === color && v.parentId === null
      );
      if (colorVar) {
        if (ageRange) {
          // Find the child variation based on selected ageRange
          const ageVar = product.variations.stockByVariation.find(
            (v: any) => v.parentId === colorVar.id && v.ageRange === ageRange
          );
          if (ageVar) {
            return ageVar.stock;
          }
        }
        // If no ageRange is selected, return the parent color variation stock
        return colorVar.stock;
      }
    }
  }

  // Add similar logic here if you have other variation types like "color-size" or "age-size"

  // Fallback to product's overall stock if no specific variation is matched
  return product.stock;
};

export const ProductDetails = () => {
  const { state, dispatch } = useApp();
  const { slug } = useParams<{ slug: string }>();
  const [product, setProduct] = useState<Product | null>(null);
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [selectedImage, setSelectedImage] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Front-end state for user's selected variation values
  const [selectedVariations, setSelectedVariations] = useState<{
    color?: string;
    size?: string;
    ageRange?: string;
  }>({});

  const [quantity, setQuantity] = useState(1);

  // State for the Specifications modal
  const [isSpecModalOpen, setIsSpecModalOpen] = useState<boolean>(false);

  // Fetch the single product by slug from URL params
  useEffect(() => {
    const fetchProduct = async () => {
      if (slug) {
        try {
          setIsLoading(true);
          setError(null);
          // Scroll to top of the product details
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });

          const response = await productsApi.getBySlug(slug);
          if (response.data) {
            setProduct(response.data);
            // Reset selected variations, quantity, and image index for new product
            setSelectedVariations({});
            setQuantity(1);
            setSelectedImage(0);
          }
        } catch (err) {
          setError('Failed to load product details. Please try again later.');
          console.error('Error fetching product:', err);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchProduct();
  }, [slug]);

  // Fetch all products once, for the "Related Products" section
  useEffect(() => {
    const fetchAllProducts = async () => {
      try {
        const response = await productsApi.getAll();
        if (response.data) {
          setAllProducts(response.data);
        }
      } catch (err) {
        console.error('Error fetching products:', err);
      }
    };

    fetchAllProducts();
  }, []);

  // Prepare local arrays for color, size, ageRange from the product.variations if present
  const [colors, setColors] = useState<string[]>([]);
  const [sizes, setSizes] = useState<string[]>([]);
  const [ageRanges, setAgeRanges] = useState<string[]>([]);

  useEffect(() => {
    if (product?.variations) {
      if (
        product.variations.stockByVariation &&
        Array.isArray(product.variations.stockByVariation) &&
        product.variations.stockByVariation.length > 0
      ) {
        const c: string[] = [];
        const s: string[] = [];
        const a: string[] = [];
        product.variations.stockByVariation.forEach((v: any) => {
          if (v.color && !c.includes(v.color)) c.push(v.color);
          if (v.size && !s.includes(v.size)) s.push(v.size);
          if (v.ageRange && !a.includes(v.ageRange)) a.push(v.ageRange);
        });
        setColors(c);
        setSizes(s);
        setAgeRanges(a);
      } else {
        setColors(product.variations.colors || []);
        setSizes(product.variations.sizes || []);
        setAgeRanges(product.variations.ageRanges || []);
      }
    }
  }, [product]);

  // Memoized arrays for available picks, e.g., filter age options by selected color if needed
  const availableColors = useMemo(() => colors, [colors]);

  /**
   * For "color-age" or "all" product types: we want to only show ages
   * that are actual children of the selected color node. This means:
   * 1) Find the color variation row => e.g. ( {id: "76", color: "Blue", parentId: null} )
   * 2) Then find all child rows that have parentId = "76" and an ageRange value.
   * 3) Return the unique ageRange strings from those child nodes.
   */
  const availableAges = useMemo(() => {
    if (
      product?.variations &&
      (product.variations.type === 'color-age' || product.variations.type === 'all') &&
      selectedVariations.color
    ) {
      // 1) Find the color node in stockByVariation
      const colorNode = product.variations.stockByVariation.find(
        (v: any) => v.color === selectedVariations.color
      );
      if (colorNode && colorNode.id) {
        // 2) Filter for child nodes with parentId = colorNode.id
        const childAges: string[] = product.variations.stockByVariation
          .filter(
            (child: any) =>
              child.parentId === colorNode.id && child.ageRange
          )
          .map((child: any) => child.ageRange);

        if (childAges.length > 0) {
          // Return only those ages that exist under the chosen color
          return Array.from(new Set(childAges));
        }
      }
    }
    // Fallback to original logic: show all known ageRanges
    return ageRanges;
  }, [product, selectedVariations.color, ageRanges]);

  /**
   * For "color-size" or "age-size" or "all" product types: we want to only show sizes
   * that are children of the selected parent node. For instance, if it's color-size,
   * we find the selected color node, then find children with "size".
   * If it's age-size, we do the same for the selected "ageRange".
   * If it's color-age-size ("all"), we might do an even deeper check
   * but let's keep it simple: if color is chosen, we filter sizes by that color node.
   * If age is chosen, we filter by that age node, etc.
   */
  const availableSizes = useMemo(() => {
    // If product type is "all" or has "size" in it, we do deeper logic
    if (product?.variations && product.variations.type.includes('size')) {
      // If there's a color dimension chosen (and no age dimension), we can filter by the color node
      if (selectedVariations.color && !selectedVariations.ageRange) {
        const colorNode = product.variations.stockByVariation.find(
          (v: any) => v.color === selectedVariations.color
        );
        if (colorNode && colorNode.id) {
          // find child nodes whose parentId = colorNode.id and have a size
          const childSizes: string[] = product.variations.stockByVariation
            .filter((child: any) => child.parentId === colorNode.id && child.size)
            .map((child: any) => child.size);
          if (childSizes.length > 0) {
            return Array.from(new Set(childSizes));
          }
        }
      }

      // If there's both a color and an age selected, we might look up the leaf node for color->age
      // then from that node, see if there's a child with size, etc. 
      // For simplicity, if you only have color->age in your data, you might skip deeper logic.
      // But if you have color->age->size, you'd do a two-step approach: color => child age => child size.

      // If no color or age is chosen yet, fall back to showing all sizes
      return sizes;
    }

    // If product type doesn't actually include "size", just return what we had
    return sizes;
  }, [product, selectedVariations.color, selectedVariations.ageRange, sizes]);

  // Build the gallery based on color-level variation
  const gallery = useMemo(() => {
    if (!product) return [];
    return getVariationSpecificImages(product, selectedVariations.color);
  }, [product, selectedVariations.color]);

  // When color changes, reset lower dimension selections & image index + set quantity=1
  const handleColorSelect = (color: string) => {
    setSelectedVariations((prev) => ({
      ...prev,
      color,
      ageRange: undefined,
      size: undefined
    }));
    setSelectedImage(0);
    setQuantity(1); // Reset quantity to 1 whenever top-level variation changes
  };

  // Similarly, if you had explicit handlers for age or size, you'd reset quantity there too.
  // Since you set them inline, just add setQuantity(1) in the onClick of your age/size buttons.

  // Adjust quantity up/down, respecting the available stock for the chosen combination
  const handleQuantityChange = (newQuantity: number) => {
    if (!product || newQuantity < 1) return;

    const stockAvailable = getAvailableStock(product, selectedVariations);
    if (newQuantity > stockAvailable) {
      toast.error(`Only ${stockAvailable} items available for selected variation`);
      return;
    }
    setQuantity(newQuantity);
  };

  // New handler for "Buy it Now" with variation validations
  const handleBuyNow = () => {
    if (!product) return;

    // Validate required variations (if applicable)
    if (availableColors.length > 0 && !selectedVariations.color) {
      toast.error('Please select a color');
      return;
    }
    if (availableAges.length > 0 && !selectedVariations.ageRange) {
      toast.error('Please select an age range');
      return;
    }
    if (availableSizes.length > 0 && !selectedVariations.size) {
      toast.error('Please select a size');
      return;
    }

    const buyNowItem = {
      ...product,
      quantity,
      selectedVariations: Object.keys(selectedVariations).length > 0 ? selectedVariations : undefined,
    };

    // Store the buyNowItem in sessionStorage (without affecting the cart)
    sessionStorage.setItem('buyNowItem', JSON.stringify(buyNowItem));

    // Navigate directly to the checkout page
    navigate('/checkout');
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">Loading product details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center text-red-500">{error}</div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">Product not found</div>
      </div>
    );
  }

  // Build related products: same category, excluding current item
  const relatedProducts = allProducts
    .filter((p) => p.category_id === product.category_id && p.id !== product.id)
    .slice(0, 4);

  const isInWishlist = state.wishlist.some((item) => item.id === product.id);

  // Add to cart with validation for required variation selections
  const handleAddToCart = () => {
    if (availableColors.length > 0 && !selectedVariations.color) {
      toast.error('Please select a color');
      return;
    }
    if (availableAges.length > 0 && !selectedVariations.ageRange) {
      toast.error('Please select an age range');
      return;
    }
    if (availableSizes.length > 0 && !selectedVariations.size) {
      toast.error('Please select a size');
      return;
    }

    dispatch({
      type: 'ADD_TO_CART',
      payload: {
        ...product,
        quantity,
        selectedVariations:
          Object.keys(selectedVariations).length > 0 ? selectedVariations : undefined
      }
    });
    toast.success('Added to cart successfully!');
  };

  // Wishlist toggling
  const handleToggleWishlist = () => {
    dispatch({ type: 'TOGGLE_WISHLIST', payload: product });
    toast.success(isInWishlist ? 'Removed from wishlist' : 'Added to wishlist');
  };

  // Navigate back to shop
  const handleBackToShop = () => {
    navigate('/shop');
  };

  // Basic share function
  const handleShare = async () => {
    const shareData = {
      title: product.name,
      text: `Check out ${product.name} on Shynerz Baby Shop`,
      url: window.location.href
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        // Fallback: copy link to clipboard
        const shareText = `${shareData.title}\n${shareData.text}\n${shareData.url}`;
        await navigator.clipboard.writeText(shareText);
        toast.success('Link copied to clipboard!');
      }
    } catch (error) {
      console.error('Error sharing:', error);
      toast.error('Failed to share');
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Back to Shop */}
      <button
        onClick={handleBackToShop}
        className="flex items-center gap-2 text-gray-600 hover:text-pink-500 mb-6"
      >
        <ChevronLeft className="h-5 w-5" />
        Back to Shop
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr,0.8fr] gap-12">
        {/* Left side - Image carousel */}
        <div className="lg:sticky lg:top-8 lg:self-start space-y-4 lg:space-y-0">
          <div className="flex flex-col-reverse lg:flex-row lg:gap-4">
            {/* Thumbnails - vertical for desktop, horizontal for mobile */}
            {gallery.length > 1 && (
              <div className="mt-4 lg:mt-0 lg:w-20 overflow-x-auto lg:overflow-y-auto lg:h-[500px] flex-shrink-0">
                <div className="flex lg:flex-col gap-4">
                  {gallery.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 ${
                        selectedImage === index ? 'ring-2 ring-pink-500' : ''
                      }`}
                    >
                      <img
                        src={image}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Main image container */}
            <div className="relative aspect-square overflow-hidden rounded-lg bg-gray-100 lg:w-[calc(100%-5rem)]">
              {gallery.length > 0 ? (
                <img
                  src={gallery[selectedImage]}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="flex items-center justify-center w-full h-full text-gray-400">
                  No Image
                </div>
              )}
              {gallery.length > 1 && (
                <>
                  <button
                    onClick={() =>
                      setSelectedImage((prev) =>
                        prev === 0 ? gallery.length - 1 : prev - 1
                      )
                    }
                    className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/80 hover:bg-white shadow-md"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() =>
                      setSelectedImage((prev) =>
                        prev === gallery.length - 1 ? 0 : prev + 1
                      )
                    }
                    className="absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/80 hover:bg-white shadow-md"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Right side - Product info */}
        <div className="lg:max-h-[calc(100vh-8rem)] lg:overflow-y-auto space-y-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{product.name}</h1>
            <p className="text-lg text-gray-500 mt-2">
              Category: {product.category_name || `ID: ${product.category_id}`}
            </p>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
              ))}
            </div>
            <span className="text-gray-500">(4.8/5 based on 120 reviews)</span>
          </div>

          <p className="text-3xl font-bold text-pink-500">
            KES {product.price.toLocaleString()}
          </p>

          {/* Variation options */}
          {product?.variations?.type !== 'none' && (
            <div className="space-y-4">
              {/* Color */}
              {availableColors.length > 0 && (
                <div>
                  <h3 className="font-medium mb-2">Color</h3>
                  <div className="flex flex-wrap gap-2">
                    {availableColors.map((color) => (
                      <button
                        key={color}
                        onClick={() => handleColorSelect(color)}
                        className={`px-4 py-2 rounded-full border ${
                          selectedVariations.color === color
                            ? 'border-pink-500 bg-pink-50 text-pink-500'
                            : 'border-gray-300 hover:border-pink-500'
                        }`}
                      >
                        {color}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Age Range */}
              {availableAges.length > 0 && (
                <div>
                  <h3 className="font-medium mb-2">Age Range</h3>
                  <div className="flex flex-wrap gap-2">
                    {availableAges.map((age) => (
                      <button
                        key={age}
                        onClick={() => {
                          // Reset the size and quantity as well
                          setSelectedVariations((prev) => ({
                            ...prev,
                            ageRange: age,
                            size: undefined
                          }));
                          setQuantity(1);
                        }}
                        className={`px-4 py-2 rounded-full border ${
                          selectedVariations.ageRange === age
                            ? 'border-pink-500 bg-pink-50 text-pink-500'
                            : 'border-gray-300 hover:border-pink-500'
                        }`}
                      >
                        {age}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Size */}
              {availableSizes.length > 0 && (
                <div>
                  <h3 className="font-medium mb-2">Size</h3>
                  <div className="flex flex-wrap gap-2">
                    {availableSizes.map((size) => (
                      <button
                        key={size}
                        onClick={() => {
                          setSelectedVariations((prev) => ({ ...prev, size }));
                          setQuantity(1);
                        }}
                        className={`px-4 py-2 rounded-full border ${
                          selectedVariations.size === size
                            ? 'border-pink-500 bg-pink-50 text-pink-500'
                            : 'border-gray-300 hover:border-pink-500'
                        }`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Quantity and Add to Cart */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 border rounded-full px-4 py-2">
                <button
                  onClick={() => handleQuantityChange(quantity - 1)}
                  className="p-1 rounded-full hover:bg-gray-100"
                >
                  -
                </button>
                <span className="w-12 text-center">{quantity}</span>
                <button
                  onClick={() => handleQuantityChange(quantity + 1)}
                  className="p-1 rounded-full hover:bg-gray-100"
                >
                  +
                </button>
              </div>
              <button
                onClick={handleAddToCart}
                className="flex-1 bg-pink-500 text-white px-8 py-3 rounded-full font-semibold hover:bg-pink-600 transition-colors flex items-center justify-center gap-2"
              >
                <ShoppingCart className="h-5 w-5" />
                Add to Cart
              </button>
            </div>
            <div>
              <button
                onClick={handleBuyNow}
                className="w-full border border-green-500 text-green-500 bg-transparent px-6 py-2 rounded-full font-semibold hover:bg-green-50 transition-colors flex items-center justify-center gap-2 pulsate"
              >
                Buy it Now
              </button>
            </div>
          </div>

          {/* Actions row: Specs, Wishlist, and Share */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => setIsSpecModalOpen(true)}
              className="flex-1 bg-pink-500 text-white px-8 py-3 rounded-full font-semibold hover:bg-pink-600 transition-colors flex items-center justify-center gap-2"
            >
              View Specifications
            </button>
            <button
              onClick={handleToggleWishlist}
              className={`p-3 rounded-full ${
                isInWishlist ? 'bg-pink-500 text-white' : 'bg-gray-100 text-gray-600'
              } hover:bg-pink-600 hover:text-white transition-colors`}
            >
              <Heart className="h-5 w-5" />
            </button>
            <button
              onClick={handleShare}
              className="p-3 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
            >
              <Share2 className="h-5 w-5" />
            </button>
          </div>

          {/* Product Description */}
          <div className="product-description text-gray-600">
            {product.description ? (
              parse(product.description)
            ) : (
              <p>No description available</p>
            )}
          </div>

          {/* Features row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-6 border-t">
            <div className="flex items-center gap-2 text-gray-600">
              <Truck className="h-5 w-5 text-pink-500" />
              <span>Countrywide Delivery</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <Shield className="h-5 w-5 text-pink-500" />
              <span>Premium Quality</span>
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <RefreshCw className="h-5 w-5 text-pink-500" />
              <span>14-Day Returns</span>
            </div>
          </div>
        </div>
      </div>

      {/* Related Products */}
      <div className="mt-16">
        <h2 className="text-2xl font-bold mb-6">Related Products</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {relatedProducts.map((rp) => (
            <div key={rp.id}>
              <ProductCard product={rp} />
            </div>
          ))}
        </div>
      </div>

      {/* Specs Modal */}
      {isSpecModalOpen && product && (
        <ProductSpecificationsModal
          productId={product.id}
          onClose={() => setIsSpecModalOpen(false)}
        />
      )}
    </div>
  );
};

export default ProductDetails;
